<ng-container *ngIf="activeScorecard() as scorecard">
  <!-- Backdrop -->
  <div class="sc-backdrop" [class.sc-backdrop-closing]="isClosing" (click)="close()"></div>

  <!-- Panel -->
  <aside class="sc-panel d-flex flex-column" [class.sc-panel-closing]="isClosing">
    <!-- Header: gradient -->
    <div class="sc-header d-flex flex-column align-items-center gap-3 position-relative">
      <button class="sc-close-btn" type="button" (click)="close()" aria-label="Close">
        <span class="material-icons">close</span>
      </button>

      <div class="d-flex flex-column align-items-center gap-2 w-100">
        <!-- Streak header: large count + fire icon -->
        <ng-container *ngIf="isStreakType(); else nonStreakIcons">
          <p class="sc-name mb-0 text-center w-100">{{ scorecard.Name }}</p>
          <div class="sc-streak-display d-flex align-items-center justify-content-center gap-2">
            <span class="sc-streak-count">{{ scorecard.ActionsCompleted }}</span>
            <img
              [src]="'placeholders/fireicon.svg' | contentAssetImage"
              alt=""
              class="sc-streak-fire"
              [class.sc-streak-fire-color]="scorecard.ActionsCompleted > 0"
            />
          </div>
          <p class="sc-desc mb-0 text-center w-100">{{ scorecard.ShortDescription }}</p>
        </ng-container>

        <!-- Non-streak icon section -->
        <ng-template #nonStreakIcons>
          <!-- Linked badge carousel (multiple linked scorecards) -->
          <div class="sc-carousel-wrap w-100" *ngIf="linkedScorecards().length > 1; else singleIcon">
            <div class="sc-carousel-container">
              <div class="sc-badge-track" #badgeTrack>
                <div
                  class="sc-badge-carousel-item"
                  *ngFor="let sc of linkedScorecards(); let i = index"
                  [class.selected]="i === activeIndex()"
                  (click)="selectAtIndex(i)"
                >
                  <img
                    [src]="sc.Badge ?? sc.Image | contentImage: '6'"
                    alt=""
                    class="sc-badge-carousel-img"
                    [class.sc-badge-grayscale]="!sc.Completed"
                  />
                </div>
              </div>
            </div>
            <ng-container *ngIf="scorecard.Completed">
              <span class="sc-carousel-sp sc-carousel-sp-1"></span>
              <span class="sc-carousel-sp sc-carousel-sp-2"></span>
              <span class="sc-carousel-sp sc-carousel-sp-3"></span>
            </ng-container>
          </div>

          <!-- Single icon (no linked scorecards) -->
          <ng-template #singleIcon>
            <!-- Completed: sparkle badge -->
            <div class="sc-badge-sparkle-wrap flex-shrink-0" *ngIf="scorecard.Completed">
              <img [src]="scorecard.Badge ?? scorecard.Image | contentImage: '6'" alt="" class="sc-badge-img" />
              <span class="sc-sparkle sc-sp-1"></span>
              <span class="sc-sparkle sc-sp-2"></span>
              <span class="sc-sparkle sc-sp-3"></span>
              <span class="sc-sparkle sc-sp-4"></span>
            </div>
            <!-- Active/locked: plain icon circle -->
            <div class="sc-icon-circle flex-shrink-0" *ngIf="!scorecard.Completed">
              <img [src]="scorecard.Image | contentImage: '6'" alt="" class="sc-icon-img" />
            </div>
          </ng-template>
        </ng-template>

        <div class="text-center w-100" *ngIf="!isStreakType()">
          <p class="sc-name mb-1">{{ scorecard.Name }}</p>
          <p class="sc-desc mb-0" [innerHTML]="scorecard.ShortDescription | safeHtml"></p>
        </div>
      </div>

      <div class="sc-prize-pill d-flex align-items-center justify-content-center gap-2 w-100" *ngIf="getPrizeTitle()">
        <span class="material-symbols-outlined sc-pill-icon">featured_seasonal_and_gifts</span>
        <span class="sc-pill-text">{{ getPrizeTitle() }}</span>
      </div>

      <!-- Progress row: milestone -->
      <div class="d-flex align-items-center justify-content-between w-100" *ngIf="isMilestoneType() && !scorecard.Completed">
        <span class="sc-progress-label">{{ scorecard | scorecardV2MilestoneValue | async }}</span>
        <span class="sc-progress-value">{{ scorecard.Milestone?.CustomerValue }}/{{ getMaxMilestone() }}</span>
      </div>

      <!-- Progress row: all other types (not milestone, not solo action) -->
      <div
        class="d-flex align-items-center justify-content-between w-100"
        *ngIf="!isMilestoneType() && !isSoloAction() && !isCounterType() && !isStreakType() && !scorecard.Completed"
      >
        <span class="sc-progress-label"
          >{{ scorecard.ActionsCompleted }} {{ 'fgbcl.challenges.actions.completed' | translate }}</span
        >
        <span class="sc-progress-value">{{ getProgressPercentage() }}%</span>
      </div>
    </div>

    <!-- Body: scrollable -->
    <div class="sc-body flex-grow-1 overflow-auto">
      <!-- Sponsor: centred at the top of the body in every state; links out when a link is set -->
      <ng-container *ngIf="scorecard.Sponsor?.SponsorImage">
        <a
          *ngIf="scorecard.Sponsor?.SponsorLink; else scSponsorPlain"
          class="sc-sponsor-row sc-sponsor-link d-flex align-items-center justify-content-center"
          [href]="scorecard.Sponsor?.SponsorLink"
          target="_blank"
          rel="noopener noreferrer"
          (click)="$event.stopPropagation()"
        >
          <ng-container *ngTemplateOutlet="scSponsorContent; context: { $implicit: scorecard }"></ng-container>
        </a>
        <ng-template #scSponsorPlain>
          <div class="sc-sponsor-row d-flex align-items-center justify-content-center">
            <ng-container *ngTemplateOutlet="scSponsorContent; context: { $implicit: scorecard }"></ng-container>
          </div>
        </ng-template>
      </ng-container>

      <!-- Completed state -->
      <ng-container *ngIf="scorecard.Completed">
        <div class="sc-completed-wrap d-flex flex-column align-items-center w-100 mb-4">
          <div class="d-flex flex-column align-items-center gap-1 w-100 mb-4">
            <p class="sc-congrats-title mb-0">{{ 'fgbcl.challanges.complete.title' | translate }}</p>
            <p class="sc-congrats-text mb-0 text-center w-100">
              {{ 'fgbcl.challanges.complete.description' | translate: { title: scorecard.Name ? scorecard.Name : '' } }}
            </p>
          </div>
          <button
            class="sc-more-details-btn d-flex align-items-center justify-content-center gap-1 w-100"
            type="button"
            (click)="detailsExpanded = !detailsExpanded"
          >
            <span class="material-icons">{{ detailsExpanded ? 'expand_less' : 'expand_more' }}</span>
            <span>{{ 'fgbcl.challanges.more.details' | translate }}</span>
          </button>
        </div>
      </ng-container>

      <!-- Locked state -->
      <ng-container *ngIf="scorecard.Locked && !scorecard.Completed">
        <div class="sc-locked-card d-flex flex-column align-items-center gap-2 p-4 w-100">
          <span class="material-icons sc-locked-icon">lock</span>
          <p class="sc-locked-title mb-0">{{ 'fgbcl.challenges.locked' | translate }}</p>
          <p class="sc-locked-text mb-0 text-center" *ngIf="activeScorecardParent() as parent">
            You need to complete '{{ parent.Name }}' to unlock this challenge
          </p>
        </div>
      </ng-container>

      <!-- Body content: always visible for active (not locked), animated for completed -->
      <div
        class="sc-details-body"
        [class.sc-details-body-open]="(!scorecard.Locked && !scorecard.Completed) || (scorecard.Completed && detailsExpanded)"
        [class.sc-details-body-no-clip]="!scorecard.Locked && !scorecard.Completed"
        [class.sc-details-body-switching]="isSwitching"
      >
        <!-- Milestone body: per-row layout so dots align with cards -->
        <div class="mb-4" *ngIf="isMilestoneType(); else actionBody">
          <p class="sc-section-label pb-3">
            <strong>{{ 'fgbcl.challanges.progress' | translate }}</strong>
          </p>
          <ng-container *ngFor="let milestone of milestoneActions(); let i = index; let last = last">
            <div class="d-flex">
              <!-- Timeline column: dot + connecting bar (leader floats above first dot via absolute) -->
              <div class="ms-col d-flex flex-column align-items-center" [class.position-relative]="i === 0">
                <div class="ms-line-leader" *ngIf="i === 0" [ngClass]="getMsLineClass(0)"></div>
                <div class="ms-dot flex-shrink-0" [ngClass]="getMsDotClass(i)"></div>
                <div class="ms-line flex-grow-1" *ngIf="!last" [ngClass]="getMsLineClass(i)"></div>
              </div>

              <!-- Card -->
              <div class="flex-grow-1 ps-3" [class.pb-3]="!last">
                <div
                  class="sc-action-card"
                  [ngClass]="
                    isMilestoneReached(milestone.Value)
                      ? 'sc-milestone-reached d-flex align-items-center justify-content-between'
                      : 'sc-milestone-future d-flex align-items-center gap-3'
                  "
                >
                  <div class="d-flex align-items-center gap-3">
                    <p class="sc-action-name mb-0">{{ milestone.Content[0]?.Name }}</p>
                  </div>
                  <span class="material-icons sc-done-icon flex-shrink-0" *ngIf="isMilestoneReached(milestone.Value)">done</span>
                </div>
              </div>
            </div>
          </ng-container>
        </div>

        <!-- Multi/solo action body -->
        <ng-template #actionBody>
          <!-- Solo action: centered card, no icon, no section header -->
          <ng-container *ngIf="isSoloAction()">
            <div class="d-flex flex-column gap-3 mb-4">
              <div
                class="sc-action-card sc-solo-action d-flex align-items-center justify-content-center"
                *ngFor="let action of todoActions()"
              >
                <p class="sc-action-name mb-0">{{ action.Content?.[0]?.Name || scorecard.ShortDescription }}</p>
              </div>
              <div
                class="sc-action-card sc-action-done d-flex align-items-center justify-content-between"
                *ngFor="let action of completedActions()"
              >
                <p class="sc-action-name mb-0">{{ action.Content?.[0]?.Name || scorecard.ShortDescription }}</p>
                <span class="material-icons sc-done-icon flex-shrink-0">done</span>
              </div>
            </div>
          </ng-container>

          <!-- Single action: circle grid -->
          <ng-container *ngIf="isSingleActionType()">
            <p class="sc-section-label mb-3" *ngIf="allActions().length > 5">
              <strong>{{ 'fgbcl.challanges.progress' | translate }}</strong>
            </p>
            <div
              class="sc-action-circles mb-4"
              [class.sc-circles-center]="allActions().length <= 2"
              [class.sc-circles-flex-between]="allActions().length > 2 && allActions().length <= 5"
              [class.sc-circles-grid]="allActions().length > 5"
            >
              <div
                class="sc-action-circle flex-shrink-0"
                *ngFor="let action of allActions()"
                [class.sc-circle-done]="action.ActionCompleted"
                [class.sc-circle-todo]="!action.ActionCompleted"
              >
                <img [src]="action.Image | contentImage: '6'" alt="" class="sc-circle-img" />
              </div>
            </div>
          </ng-container>

          <!-- Counter type -->
          <ng-container *ngIf="isCounterType()">
            <div class="sc-counter-card d-flex align-items-center justify-content-between mb-4">
              <span class="sc-counter-label">{{ 'fgbcl.challenges.total.completed' | translate }}</span>
              <span class="sc-counter-value">{{ scorecard.ActionsCompleted }}</span>
            </div>
          </ng-container>

          <!-- Multi-action: To do -->
          <div class="mb-4" *ngIf="!isSoloAction() && !isSingleActionType() && !isCounterType() && todoActions().length">
            <p class="sc-section-label mb-3">
              <strong>{{ 'fgbcl.challenges.todo' | translate }}</strong
              ><span class="fw-normal"> &ndash; {{ todoActions().length }}</span>
            </p>
            <div class="d-flex flex-column gap-3">
              <fgbcl-scorecards-v2-action
                *ngFor="let action of todoActions()"
                [action]="action"
                (linkFollowed)="close()"
              ></fgbcl-scorecards-v2-action>
            </div>
          </div>

          <!-- Multi-action: Completed -->
          <div class="mb-4" *ngIf="!isSoloAction() && !isSingleActionType() && !isCounterType() && completedActions().length">
            <p class="sc-section-label mb-3">
              <strong>{{ 'fgbcl.challenges.completed' | translate }}</strong
              ><span class="fw-normal"> &ndash; {{ completedActions().length }}</span>
            </p>
            <div class="d-flex flex-column gap-3">
              <fgbcl-scorecards-v2-action *ngFor="let action of completedActions()" [action]="action"></fgbcl-scorecards-v2-action>
            </div>
          </div>
        </ng-template>

        <!-- Description + T&Cs -->
        <div class="sc-info-card p-3">
          <!-- Description -->
          <div>
            <p class="sc-info-heading mb-2">{{ 'fgbcl.challenges.description' | translate }}</p>
            <p
              class="sc-info-text mb-1"
              [class.sc-info-clamped]="!descExpanded"
              [innerHTML]="scorecard.Description | safeHtml"
            ></p>
            <button
              class="sc-read-more-btn"
              type="button"
              (click)="descExpanded = !descExpanded"
              *ngIf="(scorecard.Description || '').length > 200"
            >
              {{ descExpanded ? 'Read less' : 'Read more' }}
            </button>
          </div>

          <!-- T&Cs -->
          <ng-container *ngIf="scorecard.ShowTermsAndConditions && scorecard.TermsAndConditions">
            <div class="sc-info-divider my-3"></div>
            <div>
              <button
                class="sc-tc-toggle d-flex align-items-center justify-content-between w-100"
                type="button"
                (click)="tcsExpanded = !tcsExpanded"
              >
                <span class="sc-info-text">{{ 'fgbcl.terms.and.conditions' | translate }}</span>
                <span class="material-icons sc-tc-chevron" [class.sc-tc-chevron-open]="tcsExpanded">expand_more</span>
              </button>
              <div class="sc-tc-body" [class.sc-tc-body-open]="tcsExpanded">
                <p class="sc-info-text mt-2 mb-0">{{ scorecard.TermsAndConditions }}</p>
              </div>
            </div>
          </ng-container>
        </div>
      </div>
    </div>
  </aside>
</ng-container>

<ng-template #scSponsorContent let-scorecard>
  <span class="sc-sponsor-label" *ngIf="scorecard.Sponsor?.SponsorText">{{ scorecard.Sponsor?.SponsorText }}</span>
  <img
    class="sc-sponsor-img flex-shrink-0"
    [src]="scorecard.Sponsor?.SponsorImage | contentImage: '1'"
    [alt]="scorecard.Sponsor?.SponsorText || ''"
  />
</ng-template>
